import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
	EmbeddedCheckoutProvider,
	EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import { HelmCompass } from "@/components/helm-logo";
import { Spinner } from "@/components/ui/spinner";
import { API_BASE_URL, STRIPE_PUBLISHABLE_KEY } from "@/lib/consts";
import { Lock, Shield } from "lucide-react";
import { trackCheckoutPageViewed } from "@/lib/analytics-events";

// --- Stripe singleton ---

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

// --- Types ---

interface CheckoutPrice {
	price_id: string;
	unit_amount: number | null;
	currency: string;
	recurring_interval: string | null;
	price_type: "recurring" | "one_time";
}

interface CheckoutProduct {
	product_id: string;
	product_name: string;
	prices: CheckoutPrice[];
}

interface CheckoutPageData {
	email: string;
	customer_name: string | null;
	products: CheckoutProduct[];
}

// --- Helpers ---

function formatCurrency(cents: number): string {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
	}).format(cents / 100);
}

// Read-only decode of the JWT payload so we can pin selection to the prices
// the backend signed into the token. Signature is validated server-side; here
// we only need the payload shape.
function decodeTokenPriceIds(token: string): Set<string> {
	try {
		const parts = token.split(".");
		if (parts.length !== 3) return new Set();
		const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
		const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
		const payload = JSON.parse(atob(padded)) as {
			items?: Array<{ price_id?: string }>;
		};
		return new Set(
			(payload.items || []).map((i) => i.price_id || "").filter(Boolean),
		);
	} catch {
		return new Set();
	}
}

// --- Route definition ---

export const Route = createFileRoute("/checkout/$token")({
	component: CheckoutPage,
});

// --- Main component ---

function CheckoutPage() {
	const { token } = Route.useParams();
	const [pageData, setPageData] = useState<CheckoutPageData | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [selectedPrices, setSelectedPrices] = useState<
		Record<string, string>
	>({});
	const [clientSecret, setClientSecret] = useState<string | null>(null);
	const [sessionLoading, setSessionLoading] = useState(false);
	const sessionAbortRef = useRef<AbortController | null>(null);

	// Fetch checkout page data on mount
	useEffect(() => {
		fetch(`${API_BASE_URL}/checkout/${token}`)
			.then(async (res) => {
				if (!res.ok) {
					const body = await res.json().catch(() => null);
					throw new Error(body?.detail || "Invalid checkout link");
				}
				return res.json();
			})
			.then((data: CheckoutPageData) => {
				setPageData(data);
				// Pin the selection to exactly the price(s) the token specifies.
				// The customer doesn't choose monthly vs one-time on the page; the
				// link is authoritative.
				const tokenPriceIds = decodeTokenPriceIds(token);
				const defaults: Record<string, string> = {};
				for (const product of data.products) {
					const tokenPrice = product.prices.find((p) =>
						tokenPriceIds.has(p.price_id),
					);
					const defaultPrice = tokenPrice ?? product.prices[0];
					defaults[product.product_id] = defaultPrice?.price_id || "";
					if (defaultPrice) {
						trackCheckoutPageViewed(
							product.product_name,
							defaultPrice.price_type,
							defaultPrice.unit_amount || 0,
						);
					}
				}
				setSelectedPrices(defaults);
			})
			.catch((err) => setError(err.message))
			.finally(() => setLoading(false));
	}, [token]);

	// Create / refresh embedded checkout session when prices change
	const createSession = useCallback(
		async (prices: Record<string, string>) => {
			if (Object.keys(prices).length === 0) return;

			// Abort any in-flight session creation
			if (sessionAbortRef.current) {
				sessionAbortRef.current.abort();
			}
			const controller = new AbortController();
			sessionAbortRef.current = controller;

			setSessionLoading(true);
			try {
				const res = await fetch(
					`${API_BASE_URL}/checkout/${token}/session`,
					{
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({
							selected_prices: Object.values(prices).map(
								(price_id) => ({ price_id, quantity: 1 }),
							),
						}),
						signal: controller.signal,
					},
				);
				if (!res.ok) {
					const body = await res.json().catch(() => null);
					throw new Error(
						body?.detail || "Failed to create checkout session",
					);
				}
				const data = await res.json();
				setClientSecret(data.client_secret);
			} catch (err) {
				if (err instanceof DOMException && err.name === "AbortError") {
					return; // Ignore aborted requests
				}
				setError(
					err instanceof Error
						? err.message
						: "Failed to create session",
				);
			} finally {
				setSessionLoading(false);
			}
		},
		[token],
	);

	// Auto-create session once page data + default prices are set
	useEffect(() => {
		if (pageData && Object.keys(selectedPrices).length > 0) {
			createSession(selectedPrices);
		}
	}, [selectedPrices, pageData, createSession]);

	// --- Loading state ---
	if (loading) {
		return (
			<div className="min-h-dvh flex items-center justify-center bg-background">
				<div className="flex flex-col items-center gap-4">
					<Spinner className="size-8 text-primary" />
					<p className="text-muted-foreground font-sans text-sm">
						Loading checkout...
					</p>
				</div>
			</div>
		);
	}

	// --- Error state ---
	if (error || !pageData) {
		return (
			<div className="min-h-dvh flex items-center justify-center bg-background px-6">
				<div className="text-center space-y-4 max-w-sm">
					<HelmCompass
						size={40}
						className="mx-auto"
						color="hsl(22 82% 31%)"
					/>
					<h1 className="font-serif text-2xl font-normal tracking-tight text-foreground">
						Checkout Unavailable
					</h1>
					<p className="text-muted-foreground font-sans">
						{error || "This checkout link is invalid."}
					</p>
				</div>
			</div>
		);
	}

	// --- Compute pricing info ---
	const totalAmount = Object.entries(selectedPrices).reduce(
		(sum, [productId, priceId]) => {
			const product = pageData.products.find(
				(p) => p.product_id === productId,
			);
			const price = product?.prices.find((p) => p.price_id === priceId);
			return sum + (price?.unit_amount || 0);
		},
		0,
	);

	const hasRecurring = Object.entries(selectedPrices).some(
		([productId, priceId]) => {
			const product = pageData.products.find(
				(p) => p.product_id === productId,
			);
			const price = product?.prices.find((p) => p.price_id === priceId);
			return price?.price_type === "recurring";
		},
	);

	return (
		<div className="min-h-dvh bg-background">
			{/* Header */}
			<header className="border-b bg-background/95 backdrop-blur px-6 py-4">
				<div className="max-w-6xl mx-auto flex items-center gap-3">
					<HelmCompass
						size={24}
						color="hsl(22 82% 31%)"
						strokeWidth={1.2}
					/>
					<span
						className="font-display text-sm"
						style={{ letterSpacing: "0.18em" }}
					>
						H E L M
					</span>
				</div>
			</header>

			{/* Two-column layout */}
			<div className="max-w-6xl mx-auto grid lg:grid-cols-[420px_1fr] gap-0 lg:gap-12 p-6 lg:p-10">
				{/* Left: Order Summary */}
				<div className="space-y-6 lg:sticky lg:top-10 lg:self-start">
					{/* Greeting + headline */}
					<div>
						{pageData.customer_name && (
							<p className="text-muted-foreground font-sans text-[15px] mb-1">
								Hi {pageData.customer_name},
							</p>
						)}
						<h1 className="font-serif text-2xl font-normal tracking-tight text-foreground">
							{hasRecurring
								? "Subscribe to"
								: "Purchase"}{" "}
							{pageData.products[0]?.product_name || "Helm"}
						</h1>
						{/* Large price display */}
						<p className="font-serif text-[32px] font-normal text-foreground mt-2">
							{formatCurrency(totalAmount)}
							{hasRecurring && (
								<span className="font-sans text-base font-normal text-muted-foreground ml-1">
									per month
								</span>
							)}
						</p>
					</div>

					{/* Product cards */}
					<div className="space-y-4">
						{pageData.products.map((product) => {
							const selectedPriceId =
								selectedPrices[product.product_id];
							const selectedPrice = product.prices.find(
								(p) => p.price_id === selectedPriceId,
							);

							return (
								<div
									key={product.product_id}
									className="rounded-lg border bg-card p-5 space-y-4"
								>
									{/* Product name + price */}
									<div className="flex justify-between items-start">
										<div>
											<h3 className="font-sans font-medium text-foreground text-[15px]">
												{product.product_name}
											</h3>
											<p className="font-sans text-sm text-muted-foreground mt-0.5">
												{selectedPrice?.price_type === "recurring"
													? "Billed monthly"
													: "One-time payment"}
											</p>
										</div>
										{selectedPrice?.unit_amount != null && (
											<span className="font-sans text-lg font-semibold text-foreground">
												{formatCurrency(
													selectedPrice.unit_amount,
												)}
												{selectedPrice.recurring_interval && (
													<span className="text-sm font-normal text-muted-foreground">
														/
														{selectedPrice.recurring_interval}
													</span>
												)}
											</span>
										)}
									</div>

								</div>
							);
						})}
					</div>

					{/* Subtotal + Total section */}
					<div className="border-t border-border pt-4 space-y-3">
						<div className="flex justify-between items-center font-sans text-sm text-muted-foreground">
							<span>Subtotal</span>
							<span>{formatCurrency(totalAmount)}</span>
						</div>
						<div className="flex justify-between items-center font-sans">
							<span className="font-medium text-foreground">
								Total due today
							</span>
							<div className="text-right">
								<span className="text-xl font-semibold text-foreground">
									{formatCurrency(totalAmount)}
								</span>
								{hasRecurring && (
									<span className="text-sm text-muted-foreground ml-1">
										/mo
									</span>
								)}
							</div>
						</div>
					</div>

					{/* Security note */}
					<div className="flex items-start gap-2.5 px-1">
						<Lock className="size-3.5 text-muted-foreground mt-0.5 shrink-0" />
						<p className="text-xs text-muted-foreground font-sans leading-relaxed">
							Payments are securely processed by Stripe. Your
							payment information is encrypted and never stored on
							our servers.
						</p>
					</div>
				</div>

				{/* Right: Embedded Stripe Checkout */}
				<div className="mt-6 lg:mt-0">
					{sessionLoading && !clientSecret ? (
						<div className="rounded-lg border bg-card p-12 flex items-center justify-center min-h-[400px]">
							<div className="flex flex-col items-center gap-4">
								<Spinner className="size-6 text-primary" />
								<p className="text-muted-foreground font-sans text-sm">
									Preparing payment form...
								</p>
							</div>
						</div>
					) : clientSecret ? (
						<div className="rounded-lg border bg-card overflow-hidden">
							<EmbeddedCheckoutProvider
								stripe={stripePromise}
								options={{ clientSecret }}
							>
								<EmbeddedCheckout />
							</EmbeddedCheckoutProvider>
						</div>
					) : null}

					{/* Switching overlay */}
					{sessionLoading && clientSecret && (
						<div className="fixed inset-0 bg-background/50 backdrop-blur-sm z-50 flex items-center justify-center">
							<div className="bg-card rounded-lg border p-6 flex flex-col items-center gap-3 shadow-lg">
								<Spinner className="size-5 text-primary" />
								<p className="text-sm font-sans text-muted-foreground">
									Updating payment form...
								</p>
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Footer */}
			<footer className="border-t bg-background px-6 py-4 mt-8">
				<div className="max-w-6xl mx-auto flex items-center justify-between">
					<div className="flex items-center gap-2 text-xs text-muted-foreground font-sans">
						<Shield className="size-3.5" />
						<span>Secure checkout</span>
					</div>
					<p className="text-xs text-muted-foreground font-sans">
						Helm by American Majority Project
					</p>
				</div>
			</footer>
		</div>
	);
}
